import type { ComponentType, ReactNode } from "react";

import { Image } from "@/components/shared/image";
import { Logo } from "@/components/shared/logo";
import type { MediaAsset } from "@/lib/media";
import { cn } from "@/lib/utils";

// =============================================================================
// Devices drawn in CSS. Every measure is relative to the width of the device
// (container query units), so that they scale with their panel.
// =============================================================================

type ScreenProps = { children: ReactNode };

type MockupProps = {
  image: MediaAsset;
  alt: string;
  className?: string;
  /** Wraps the screenshot (entrance animation of the screen) */
  Screen?: ComponentType<ScreenProps>;
  /** Load the screenshot right away (largest image above the fold) */
  eager?: boolean;
};

function PlainScreen({ children }: ScreenProps) {
  return children;
}

// =============================================================================

/**
 * All-in-one desktop computer: black bezel, aluminium chin and stand. The
 * stand is taller than needed: the panel crops it.
 */
export function DesktopMockup({
  image,
  alt,
  className,
  Screen = PlainScreen,
  eager = false,
}: MockupProps) {
  return (
    <div className={cn("@container", className)}>
      <div className="relative rounded-t-[3cqw] bg-[#0c0c0d] px-[4.1cqw] pt-[3.9cqw] pb-[3.6cqw] shadow-[0_4cqw_8cqw_-2cqw_rgb(0_0_0/0.55)] ring-1 ring-white/6 ring-inset">
        <span
          aria-hidden
          className="absolute top-[1.6cqw] left-1/2 size-[0.75cqw] -translate-x-1/2 rounded-full bg-[#1d262e] ring-1 ring-white/10"
        />
        <div className="relative aspect-video overflow-hidden bg-black">
          <Screen>
            <Image
              src={image.url}
              alt={alt}
              fill
              sizes="(min-width: 768px) 42vw, 80vw"
              loading={eager ? "eager" : "lazy"}
              fetchPriority={eager ? "high" : "auto"}
              className="object-cover object-top"
            />
          </Screen>
        </div>
      </div>
      <div className="flex h-[8.9cqw] items-center justify-center rounded-b-[2cqw] border-t border-white/7 bg-linear-to-b from-[#3d3d41] to-[#2a2a2d]">
        <Logo className="w-[2.2cqw] text-white/30" solid />
      </div>
      <div
        aria-hidden
        className="relative mx-auto h-[30cqw] w-[26cqw] bg-[linear-gradient(90deg,#1b1b1d,#77777c_24%,#c9c9ce_50%,#77777c_76%,#1b1b1d)] [clip-path:polygon(9%_0,91%_0,100%_100%,0_100%)]"
      >
        <span className="absolute inset-x-0 top-0 h-[22%] bg-linear-to-b from-black/70 to-transparent" />
      </div>
    </div>
  );
}

// =============================================================================

function StatusIcons() {
  return (
    <span className="flex items-center gap-[1.4cqw]" aria-hidden>
      {/* Signal */}
      <svg viewBox="0 0 18 12" className="h-[4cqw] w-auto" fill="currentColor">
        <rect x="0" y="8" width="3" height="4" rx="0.8" />
        <rect x="5" y="5.5" width="3" height="6.5" rx="0.8" />
        <rect x="10" y="3" width="3" height="9" rx="0.8" />
        <rect x="15" y="0" width="3" height="12" rx="0.8" />
      </svg>
      {/* Wi-Fi */}
      <svg viewBox="0 0 16 12" className="h-[4cqw] w-auto" fill="currentColor">
        <path d="M8 2.2c2.4 0 4.6.9 6.3 2.5l1.2-1.3A10.6 10.6 0 0 0 8 .4C5.2.4 2.6 1.5.5 3.4l1.2 1.3A8.9 8.9 0 0 1 8 2.2Zm0 3.6c1.4 0 2.7.5 3.7 1.4l1.2-1.3A7.2 7.2 0 0 0 8 4a7.2 7.2 0 0 0-4.9 1.9l1.2 1.3c1-.9 2.3-1.4 3.7-1.4Zm0 3.5c.5 0 1 .2 1.3.5L8 11.6 6.7 9.8c.3-.3.8-.5 1.3-.5Z" />
      </svg>
      {/* Battery */}
      <svg viewBox="0 0 27 13" className="h-[4cqw] w-auto" fill="none">
        <rect
          x="0.5"
          y="0.5"
          width="23"
          height="12"
          rx="3.5"
          stroke="currentColor"
          opacity="0.45"
        />
        <rect x="2" y="2" width="17" height="9" rx="2" fill="currentColor" />
        <path
          d="M25 4.5v4c.8-.3 1.4-1.1 1.4-2s-.6-1.7-1.4-2Z"
          fill="currentColor"
          opacity="0.45"
        />
      </svg>
    </span>
  );
}

/**
 * Recent phone: titanium edge, black bezel, dynamic island, status bar and
 * home indicator. Give it a height or a width, its ratio is fixed.
 */
export function PhoneMockup({
  image,
  alt,
  className,
  Screen = PlainScreen,
}: MockupProps) {
  return (
    <div className={cn("@container relative aspect-205/423", className)}>
      {/* Side buttons */}
      <span
        aria-hidden
        className="absolute top-[17%] left-[-0.9cqw] h-[4.5%] w-[1.4cqw] rounded-l-[0.6cqw] bg-[#3a3a3d]"
      />
      <span
        aria-hidden
        className="absolute top-[25%] left-[-0.9cqw] h-[8%] w-[1.4cqw] rounded-l-[0.6cqw] bg-[#3a3a3d]"
      />
      <span
        aria-hidden
        className="absolute top-[35%] left-[-0.9cqw] h-[8%] w-[1.4cqw] rounded-l-[0.6cqw] bg-[#3a3a3d]"
      />
      <span
        aria-hidden
        className="absolute top-[28%] right-[-0.9cqw] h-[12%] w-[1.4cqw] rounded-r-[0.6cqw] bg-[#3a3a3d]"
      />

      <div className="absolute inset-0 rounded-[16.5cqw] bg-[linear-gradient(145deg,#6a6a6f,#2b2b2e_28%,#1c1c1e_62%,#55555a)] p-[1.5cqw] shadow-[0_5cqw_10cqw_-2cqw_rgb(0_0_0/0.6)]">
        <div className="size-full rounded-[15cqw] bg-black p-[3cqw]">
          <div className="relative size-full overflow-hidden rounded-[12cqw] bg-[#0e0e0f]">
            {/* Status bar */}
            <div className="absolute inset-x-0 top-0 z-10 flex h-[13cqw] items-center justify-between px-[8cqw] text-[5.2cqw] leading-none font-semibold text-white">
              <span>9:41</span>
              <StatusIcons />
            </div>
            <span
              aria-hidden
              className="absolute top-[3.2cqw] left-1/2 z-10 h-[8.6cqw] w-[30cqw] -translate-x-1/2 rounded-full bg-black"
            />
            <div className="absolute inset-x-0 top-[13cqw] bottom-0 overflow-hidden">
              <Screen>
                <Image
                  src={image.url}
                  alt={alt}
                  fill
                  sizes="(min-width: 768px) 14vw, 25vw"
                  className="object-cover object-top"
                />
              </Screen>
            </div>
            <span
              aria-hidden
              className="absolute bottom-[2.4cqw] left-1/2 z-10 h-[1.3cqw] w-[34cqw] -translate-x-1/2 rounded-full bg-white/85 shadow-[0_0_2cqw_rgb(0_0_0/0.4)]"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
